import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { BookingOccupancyCalendar } from '@/components/booking/BookingOccupancyCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_LABELS = {
  "1": "Associazioni di pubblica utilit\u00e0 residenti",
  "2": "Associazioni non residenti e persone residenti",
  "3": "Non residenti"
};

export default function NewBooking() {
  const { hallId } = useParams();
  const navigate = useNavigate();
  const [hall, setHall] = useState(null);
  const [formData, setFormData] = useState({
    use_cucina: false,
    descrizione_manifestazione: '',
    tipo_manifestazione: '',
    tipo_prezzo: '3'
  });
  const [dateRanges, setDateRanges] = useState([{ start_date: '', start_time: '08:00', end_date: '', end_time: '20:00' }]);
  const [pricing, setPricing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isDachverband, setIsDachverband] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchData(); }, [hallId]);

  const fetchData = async () => {
    try {
      const timestamp = new Date().getTime();
      const [hallsRes, categoriesRes, meRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/halls?t=${timestamp}`),
        axios.get(`${API_URL}/categories?t=${timestamp}`),
        axios.get(`${API_URL}/auth/me`),
        axios.get(`${API_URL}/halls/${hallId}/bookings?t=${timestamp}`)
      ]);
      setHall(hallsRes.data.find(h => h.id === hallId));
      setBookings(bookingsRes.data);
      const activeCats = (categoriesRes.data.categories || []).filter(c => c.active);
      setCategories(activeCats);

      const userTipo = meRes.data.tipo_richiedente || '3';
      if (activeCats.length > 0) {
        setFormData(prev => ({ ...prev, tipo_manifestazione: prev.tipo_manifestazione || activeCats[0].key, tipo_prezzo: userTipo }));
      } else {
        setFormData(prev => ({ ...prev, tipo_prezzo: userTipo }));
      }

      try {
        const pricingRes = await axios.get(`${API_URL}/pricing/for-user?t=${timestamp}`);
        setPricing(pricingRes.data.pricing);
        setIsDachverband(pricingRes.data.is_dachverband);
      } catch {
        const pricingRes = await axios.get(`${API_URL}/pricing?t=${timestamp}`);
        setPricing(pricingRes.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    }
  };

  const hallPrefix = hallId === 'hall_domene_moling' ? 'domene' : 'pice';

  const getDaysCount = (dr) => {
    if (!dr.start_date || !dr.end_date) return 1;
    const start = new Date(dr.start_date);
    const end = new Date(dr.end_date);
    return Math.max(1, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1);
  };

  const getDailyPrice = () => {
    if (!pricing) return 0;
    if (formData.tipo_manifestazione === 'comune_scuole' || formData.tipo_manifestazione === 'anziani') return 0;
    let daily = 0;
    if (formData.use_cucina) daily += pricing[`cucina_${hallPrefix}_prezzo_${formData.tipo_prezzo}`] ?? 0;
    if (formData.tipo_manifestazione) daily += pricing[`${formData.tipo_manifestazione}_${hallPrefix}_prezzo_${formData.tipo_prezzo}`] ?? 0;
    return daily;
  };

  const calculateTotal = () => {
    const daily = getDailyPrice();
    if (daily === 0) return 0;
    return dateRanges.filter(dr => dr.start_date && dr.end_date).reduce((sum, dr) => sum + daily * getDaysCount(dr), 0);
  };

  const getTotalDays = () => dateRanges.filter(dr => dr.start_date && dr.end_date).reduce((sum, dr) => sum + getDaysCount(dr), 0);

  const getKitchenPrice = () => pricing ? (pricing[`cucina_${hallPrefix}_prezzo_${formData.tipo_prezzo}`] ?? 0) : 0;

  const addDateRange = () => setDateRanges([...dateRanges, { start_date: '', start_time: '08:00', end_date: '', end_time: '20:00' }]);
  const removeDateRange = (index) => { if (dateRanges.length <= 1) return; setDateRanges(dateRanges.filter((_, i) => i !== index)); };
  const updateDateRange = (index, field, value) => { const updated = [...dateRanges]; updated[index] = { ...updated[index], [field]: value }; setDateRanges(updated); };

  const handleSubmit = async () => {
    const validRanges = dateRanges.filter(dr => dr.start_date && dr.end_date);
    if (validRanges.length === 0) { toast.error('Inserisci almeno una data'); return; }
    if (!formData.tipo_manifestazione) { toast.error('Seleziona una categoria di manifestazione'); return; }
    const useCucina = formData.tipo_manifestazione === 'solo_cucina' ? true : formData.use_cucina;

    setLoading(true);
    try {
      if (validRanges.length === 1) {
        const dr = validRanges[0];
        await axios.post(`${API_URL}/bookings`, {
          hall_id: hallId, start_datetime: `${dr.start_date}T${dr.start_time}:00`, end_datetime: `${dr.end_date}T${dr.end_time}:00`,
          use_cucina: useCucina, descrizione_manifestazione: formData.descrizione_manifestazione,
          tipo_manifestazione: formData.tipo_manifestazione, tipo_prezzo: formData.tipo_prezzo
        });
        toast.success('Prenotazione creata con successo!');
      } else {
        const result = await axios.post(`${API_URL}/bookings/multiple`, {
          hall_id: hallId,
          date_ranges: validRanges.map(dr => ({ start_datetime: `${dr.start_date}T${dr.start_time}:00`, end_datetime: `${dr.end_date}T${dr.end_time}:00` })),
          use_cucina: useCucina, descrizione_manifestazione: formData.descrizione_manifestazione,
          tipo_manifestazione: formData.tipo_manifestazione, tipo_prezzo: formData.tipo_prezzo
        });
        const d = result.data;
        if (d.errors?.length > 0) toast.error(`${d.created} prenotazioni create. Errori: ${d.errors.join(', ')}`);
        else toast.success(`${d.created} prenotazioni create con successo!`);
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione della prenotazione');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
        </Button>

        <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
          <div className="text-center space-y-2">
            <h1 className="font-heading font-extrabold text-2xl sm:text-4xl lg:text-5xl tracking-tight">Nuova Prenotazione</h1>
            <p className="text-base sm:text-lg text-muted-foreground">{hall?.name}</p>
          </div>

          {/* Step 1: Date e Orari */}
          <Card className="card-hover">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-xl sm:text-2xl flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" /> 1. Date e Orari
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Puoi aggiungere pi\u00f9 date per manifestazioni ricorrenti</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <BookingOccupancyCalendar bookings={bookings} />

              {dateRanges.map((dr, index) => (
                <div key={index} className="space-y-4 p-4 rounded-lg border" data-testid={`date-range-${index}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-base">Data {dateRanges.length > 1 ? `${index + 1}` : ''}</h3>
                    {dateRanges.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeDateRange(index)} className="text-destructive" data-testid={`remove-date-${index}`}>Rimuovi</Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Data Inizio</Label><Input type="date" value={dr.start_date} onChange={(e) => updateDateRange(index, 'start_date', e.target.value)} className="h-12" data-testid={`start-date-${index}`} /></div>
                    <div className="space-y-2"><Label>Ora Inizio</Label><Input type="time" value={dr.start_time} onChange={(e) => updateDateRange(index, 'start_time', e.target.value)} className="h-12" data-testid={`start-time-${index}`} /></div>
                    <div className="space-y-2"><Label>Data Fine</Label><Input type="date" value={dr.end_date} onChange={(e) => updateDateRange(index, 'end_date', e.target.value)} className="h-12" data-testid={`end-date-${index}`} /></div>
                    <div className="space-y-2"><Label>Ora Fine</Label><Input type="time" value={dr.end_time} onChange={(e) => updateDateRange(index, 'end_time', e.target.value)} className="h-12" data-testid={`end-time-${index}`} /></div>
                  </div>
                  {dr.start_date && dr.end_date && (
                    <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                      <p className="text-sm font-medium text-primary">
                        Dal {format(new Date(dr.start_date), 'dd/MM/yyyy', { locale: it })} ore {dr.start_time} al {format(new Date(dr.end_date), 'dd/MM/yyyy', { locale: it })} ore {dr.end_time}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              <Button variant="outline" onClick={addDateRange} className="w-full" data-testid="add-date-button">+ Aggiungi un&#39;altra data</Button>
            </CardContent>
          </Card>

          {/* Step 2: Tipo Richiedente */}
          <Card className="card-hover">
            <CardHeader><CardTitle className="font-heading text-xl sm:text-2xl">2. Tipo Richiedente</CardTitle></CardHeader>
            <CardContent>
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm font-medium text-primary">{TIER_LABELS[formData.tipo_prezzo] || 'Non impostato'}</p>
                  {formData.tipo_prezzo === '1' && (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${isDachverband === true ? 'bg-green-100 text-green-800 border border-green-500' : 'bg-red-100 text-red-800 border border-red-500'}`} data-testid="dachverband-badge">
                      {isDachverband === true ? 'Socio Dachverband' : 'Non socio Dachverband (+\u20ac10 su tutte le categorie)'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Impostato dal tuo profilo. Contatta l&#39;admin per modificarlo.</p>
              </div>
            </CardContent>
          </Card>

          {/* Step 3: Cucina */}
          {hall?.has_kitchen && pricing && (
            <Card className="card-hover">
              <CardHeader><CardTitle className="font-heading text-xl sm:text-2xl">3. Uso Cucina</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <Checkbox id="use_cucina" checked={formData.use_cucina} onCheckedChange={(checked) => setFormData({ ...formData, use_cucina: checked })} data-testid="checkbox-cucina" />
                  <Label htmlFor="use_cucina" className="text-base sm:text-lg cursor-pointer">Cucina (&euro;{getKitchenPrice().toFixed(2)})</Label>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 4: Categoria Manifestazione */}
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="font-heading text-xl sm:text-2xl">4. Categoria Manifestazione</CardTitle>
              <CardDescription className="text-xs sm:text-sm">I prezzi variano in base al tipo richiedente selezionato</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="descrizione" className="text-sm sm:text-base">Descrizione Manifestazione *</Label>
                <Textarea id="descrizione" placeholder="Descrivi la tua manifestazione..." value={formData.descrizione_manifestazione} onChange={(e) => setFormData({ ...formData, descrizione_manifestazione: e.target.value })} rows={3} className="text-base" data-testid="descrizione-input" />
              </div>
              <div className="space-y-3">
                <Label className="text-sm sm:text-base font-semibold">Seleziona Categoria *</Label>
                <div className="hidden sm:grid grid-cols-[1fr_120px] gap-2 text-xs font-semibold pb-2 border-b"><div>Categoria</div><div className="text-center">Prezzo</div></div>
                <RadioGroup value={formData.tipo_manifestazione} onValueChange={(value) => setFormData({ ...formData, tipo_manifestazione: value })} data-testid="tipo-manifestazione-group" className="space-y-2">
                  {categories.map((cat) => {
                    const userPrice = pricing ? (pricing[`${cat.key}_${hallPrefix}_prezzo_${formData.tipo_prezzo}`] ?? 0) : 0;
                    return (
                      <div key={cat.key} className="flex flex-col sm:grid sm:grid-cols-[1fr_120px] gap-1 sm:gap-2 p-3 rounded-lg hover:bg-muted transition-colors border sm:border-0" data-testid={`category-option-${cat.key}`}>
                        <div className="flex items-start space-x-2">
                          <RadioGroupItem value={cat.key} id={`cat-${cat.key}`} data-testid={`radio-${cat.key}`} className="mt-1" />
                          <Label htmlFor={`cat-${cat.key}`} className="cursor-pointer font-medium text-sm sm:text-base leading-snug">{cat.label}</Label>
                        </div>
                        <div className="flex sm:justify-center sm:items-center pl-6 sm:pl-0 text-sm font-bold text-primary">&euro;{userPrice.toFixed(0)}</div>
                      </div>
                    );
                  })}
                  <div className="flex flex-col sm:grid sm:grid-cols-[1fr_120px] gap-1 sm:gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border-2 border-green-500">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="comune_scuole" id="cat-comune" data-testid="radio-comune-scuole" /><Label htmlFor="cat-comune" className="cursor-pointer font-bold text-sm sm:text-base text-green-800 dark:text-green-200">Comune/Scuole</Label></div>
                    <div className="flex sm:justify-center pl-6 sm:pl-0 text-sm font-bold text-green-700 dark:text-green-300">GRATIS</div>
                  </div>
                  <div className="flex flex-col sm:grid sm:grid-cols-[1fr_120px] gap-1 sm:gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border-2 border-green-500">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="anziani" id="cat-anziani" data-testid="radio-anziani" /><Label htmlFor="cat-anziani" className="cursor-pointer font-bold text-sm sm:text-base text-green-800 dark:text-green-200">Anziani</Label></div>
                    <div className="flex sm:justify-center pl-6 sm:pl-0 text-sm font-bold text-green-700 dark:text-green-300">GRATIS</div>
                  </div>
                  <div className="flex flex-col sm:grid sm:grid-cols-[1fr_120px] gap-1 sm:gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-400">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="solo_cucina" id="cat-solo-cucina" data-testid="radio-solo-cucina" /><Label htmlFor="cat-solo-cucina" className="cursor-pointer font-bold text-sm sm:text-base text-blue-800 dark:text-blue-200">Solo Cucina (senza sala)</Label></div>
                    <div className="flex sm:justify-center pl-6 sm:pl-0 text-sm font-bold text-blue-700 dark:text-blue-300">&euro;{getKitchenPrice().toFixed(0)}/g</div>
                  </div>
                </RadioGroup>
              </div>
            </CardContent>
          </Card>

          {/* Riepilogo */}
          <Card className="border-primary border-2">
            <CardHeader className="pb-4"><CardTitle className="font-heading text-xl sm:text-2xl">Riepilogo</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {dateRanges.some(dr => dr.start_date && dr.end_date) && (
                <div className="space-y-2 text-sm">
                  {dateRanges.filter(dr => dr.start_date && dr.end_date).map((dr, i) => (
                    <div key={i} className="flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span className="text-muted-foreground">Data {dateRanges.length > 1 ? i + 1 : ''} ({getDaysCount(dr)} {getDaysCount(dr) === 1 ? 'giorno' : 'giorni'}):</span>
                      <span className="font-medium">{format(new Date(dr.start_date), 'dd/MM/yyyy')} {dr.start_time} - {format(new Date(dr.end_date), 'dd/MM/yyyy')} {dr.end_time}</span>
                    </div>
                  ))}
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-muted-foreground">Categoria:</span>
                    <span className="font-medium">
                      {formData.tipo_manifestazione === 'comune_scuole' ? 'Comune/Scuole'
                        : formData.tipo_manifestazione === 'anziani' ? 'Anziani'
                        : formData.tipo_manifestazione === 'solo_cucina' ? 'Solo Cucina'
                        : categories.find(c => c.key === formData.tipo_manifestazione)?.label?.split(':')[0] || formData.tipo_manifestazione}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-muted-foreground">Tipo richiedente:</span>
                    <span className="font-medium">{TIER_LABELS[formData.tipo_prezzo]}</span>
                  </div>
                  {getDailyPrice() > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Prezzo giornaliero:</span>
                      <span className="font-medium">&euro;{getDailyPrice().toFixed(2)} x {getTotalDays()} {getTotalDays() === 1 ? 'giorno' : 'giorni'}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="font-semibold text-base sm:text-lg">Totale:</span>
                <span className="font-heading font-bold text-2xl sm:text-3xl text-primary" data-testid="total-price">&euro; {calculateTotal().toFixed(2)}</span>
              </div>
              <Button onClick={handleSubmit} disabled={loading || !dateRanges.some(dr => dr.start_date && dr.end_date) || !formData.descrizione_manifestazione || !formData.tipo_manifestazione} size="lg" className="w-full btn-scale text-base" data-testid="confirm-booking-button">
                {loading ? 'Creazione in corso...' : (<><CheckCircle2 className="w-5 h-5 mr-2" /> Conferma Prenotazione</>)}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
